import React, { useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Oval } from 'react-loader-spinner';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import { Colors } from 'modules/theme';
import { ClickAwayListener } from '@mui/material';

const StyledPopper = styled(Popper)(() => ({
  zIndex: 100,
}));

const StyledMenuList = styled(MenuList)(() => ({
  margin: 0,
  padding: 0,
  top: 0,
  borderRadius: '7px',
  overflowX: 'hidden',
  maxHeight: '800px',
  '&:focus': {
    outline: 'none',
  },
}));

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== 'showRightArrow' && prop !== 'arrowRightPosition',
})(({ width, showRightArrow, arrowRightPosition, styles }) => ({
  marginTop: 12,
  width: width,
  background: 'white',
  overflow: 'visible',
  borderRadius: 7,
  boxShadow: '0px 2px 16px rgba(5, 21, 40, 0.15)',
  '&::before': {
    content: "''",
    display: showRightArrow ? 'block' : 'none',
    position: 'absolute',
    top: 7,
    right: arrowRightPosition || 25,
    width: 10,
    height: 10,
    background: Colors.white,
    boxShadow: '0px 2px 16px rgba(5, 21, 40, 0.15)',
    transform: 'rotate(-45deg)',
  },
  ...styles,
}));

const LoaderContainer = styled('div')(() => ({
  display: 'flex',
  padding: 20,
  justifyContent: 'center',
  alignContent: 'center',
}));

export default function DropdownMenu({
  styles,
  open,
  loading,
  children,
  onClick,
  onClose,
  anchorEl,
  placement = 'bottom-end',
  width,
  disablePortal = true,
  showRightArrow = true,
  arrowRightPosition,
  dropdownStyle,
  ...otherProps
}) {
  const ref = useRef(null);

  function handleMenuKeyDown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (onClose) {
      onClose(event);
    }
  }

  return (
    <StyledPopper
      ref={ref}
      placement={placement}
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      disablePortal={disablePortal}
      {...otherProps}
    >
      <StyledPaper
        width={width}
        showRightArrow={showRightArrow}
        arrowRightPosition={arrowRightPosition}
        styles={styles}
      >
        <ClickAwayListener onClickAway={onClose}>
          {loading ? (
            <LoaderContainer>
              <Oval
                secondaryColor={Colors.darkBlue}
                color={Colors.darkBlue}
                width={20}
                height={20}
              />
            </LoaderContainer>
          ) : (
            <StyledMenuList
              onClick={onClick}
              onKeyDown={handleMenuKeyDown}
              style={dropdownStyle}
              className="scrollable"
            >
              {children}
            </StyledMenuList>
          )}
        </ClickAwayListener>
      </StyledPaper>
    </StyledPopper>
  );
}
